<template>
  <div class="doctor-select-wrapper">
    <!-- Main input-like container -->
    <div
      class="doctor-select-container form-control d-flex align-items-center"
      :class="{ 'is-invalid': showError && !selectedDoctor }"
    >
      <!-- Mode toggle as prepend -->
      <div class="input-group-prepend border-right mr-2 pr-2">
        <b-button-group size="sm">
          <b-button
            :variant="activeTab === 0 ? 'primary' : 'outline-secondary'"
            @click="activeTab = 0"
            class="px-2"
            :disabled="disabled"
            title="แพทย์ที่ใช้บ่อย"
          >
            <i class="fas fa-star"></i>
          </b-button>
          <b-button
            :variant="activeTab === 1 ? 'primary' : 'outline-secondary'"
            @click="activeTab = 1"
            class="px-2"
            :disabled="disabled"
            title="ค้นหาแพทย์"
          >
            <i class="fas fa-search"></i>
          </b-button>
        </b-button-group>
      </div>

      <!-- Content area -->
      <div class="flex-grow-1 d-flex align-items-center">
        <!-- Tab 0: Frequent doctors -->
        <div v-if="activeTab === 0" class="d-flex align-items-center flex-wrap">
          <template v-if="frequentDoctors.length > 0">
            <span
              v-for="doctor in frequentDoctors"
              :key="doctor.uid"
              class="frequent-doctor-item mr-2"
              :class="{
                'selected': value === doctor.uid,
                'disabled': disabled
              }"
              @click="!disabled && selectDoctor(doctor)"
            >
              {{ doctor.fullName }}
              <i
                v-if="!disabled"
                class="fas fa-times remove-icon ml-1"
                @click.stop="removeFrequent(doctor.uid)"
              ></i>
            </span>
          </template>
          <span v-else class="text-muted small">
            ยังไม่มี - เลือกจาก <i class="fas fa-search"></i>
          </span>
        </div>

        <!-- Tab 1: Search by specialty -->
        <div v-else class="d-flex flex-grow-1">
          <b-form-select
            v-model="selectedSpecialty"
            :options="specialtyOptions"
            value-field="val"
            text-field="label"
            :disabled="disabled"
            class="specialty-select border-0 mr-2"
            size="sm"
          >
            <template #first>
              <b-form-select-option :value="null">ทุกสาขา</b-form-select-option>
            </template>
          </b-form-select>
          <multiselect
            v-model="selectedDoctorObj"
            :options="filteredDoctors"
            :multiple="false"
            placeholder="ค้นหาแพทย์..."
            track-by="uid"
            label="fullName"
            :disabled="disabled"
            selectLabel=""
            deselectLabel=""
            selectedLabel=""
            class="doctor-multiselect flex-grow-1"
            @select="handleDoctorSelect"
          >
            <span slot="noResult">ไม่พบ</span>
          </multiselect>
        </div>
      </div>
    </div>

    <!-- Validation error -->
    <div v-if="!selectedDoctor && showError" class="invalid-feedback d-block">
      กรุณาเลือกแพทย์
    </div>
  </div>
</template>

<script>
export default {
  name: "DoctorSelect",
  props: {
    value: {
      type: Number,
      default: null,
    },
    doctors: {
      type: Array,
      default: () => [],
    },
    specialties: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: "md",
    },
    state: {
      type: Boolean,
      default: null,
    },
    showError: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: 0, // 0 = ใช้บ่อย, 1 = ตามสาขา
      selectedSpecialty: null,
      selectedDoctorObj: null,
      frequentDoctors: [],
    };
  },
  computed: {
    specialtyOptions() {
      return this.specialties || [];
    },

    filteredDoctors() {
      if (!this.selectedSpecialty) {
        return this.doctors;
      }
      return this.doctors.filter(d =>
        d.drSpecialItems?.includes(this.selectedSpecialty)
      );
    },

    selectedDoctor() {
      return this.doctors.find(d => d.uid === this.value) || null;
    },

    storageKey() {
      return `frequentDoctors_${this.$route?.params?.branchUrl || "default"}`;
    },
  },
  watch: {
    doctors: {
      immediate: true,
      handler(list) {
        if (list?.length > 0) {
          this.loadFromStorage();
          if (this.value) {
            this.selectedDoctorObj =
              list.find(d => d.uid === this.value) || null;
          }
        }
      },
    },

    value(uid) {
      if (uid && this.doctors.length) {
        this.selectedDoctorObj =
          this.doctors.find(d => d.uid === uid) || null;
      } else if (!uid) {
        this.selectedDoctorObj = null;
      }
    },
  },
  methods: {
    selectDoctor(doctor) {
      this.$emit("input", doctor.uid);
      this.$emit("doctor-selected", doctor);
    },

    handleDoctorSelect(doctor) {
      this.selectDoctor(doctor);
      this.addFrequent(doctor);
    },

    removeFrequent(uid) {
      this.frequentDoctors = this.frequentDoctors.filter(d => d.uid !== uid);
      this.saveToStorage();
    },

    addFrequent(doctor) {
      if (!this.frequentDoctors.find(d => d.uid === doctor.uid)) {
        this.frequentDoctors.unshift(doctor);
        if (this.frequentDoctors.length > 5) {
          this.frequentDoctors.pop();
        }
        this.saveToStorage();
      }
    },

    loadFromStorage() {
      try {
        const stored = localStorage.getItem(this.storageKey);
        if (stored) {
          const uids = JSON.parse(stored);
          this.frequentDoctors = uids
            .map(uid => this.doctors.find(d => d.uid === uid))
            .filter(Boolean);
        }
      } catch (e) {
        console.warn("Failed to load frequent doctors from storage:", e);
      }
    },

    saveToStorage() {
      try {
        const uids = this.frequentDoctors.map(d => d.uid);
        localStorage.setItem(this.storageKey, JSON.stringify(uids));
      } catch (e) {
        console.warn("Failed to save frequent doctors to storage:", e);
      }
    },

    reset() {
      this.selectedDoctorObj = null;
      this.selectedSpecialty = null;
      this.activeTab = 0;
    },
  },
};
</script>

<style scoped>
.doctor-select-container {
  min-height: 38px;
  height: auto;
  padding: 0.25rem 0.5rem;
}

.doctor-select-container .input-group-prepend {
  border-color: #ced4da;
}

/* Frequent doctor items - styled like tags */
.frequent-doctor-item {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  font-size: 0.875rem;
  color: #495057;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.frequent-doctor-item:hover:not(.disabled) {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.frequent-doctor-item.selected {
  color: #fff;
  background-color: #007bff;
  border-color: #007bff;
}

.frequent-doctor-item.disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.frequent-doctor-item .remove-icon {
  font-size: 0.7rem;
  opacity: 0.6;
  cursor: pointer;
}

.frequent-doctor-item .remove-icon:hover {
  opacity: 1;
  color: #dc3545;
}

.frequent-doctor-item.selected .remove-icon:hover {
  color: #fff;
}

/* Specialty select */
.specialty-select {
  width: 100px;
  min-width: 100px;
  background-color: transparent;
}

/* Multiselect styling to match */
.doctor-multiselect {
  min-width: 0;
}

.doctor-select-wrapper >>> .multiselect {
  min-height: 30px;
  font-size: 0.875rem;
  border: none;
}

.doctor-select-wrapper >>> .multiselect__tags {
  min-height: 30px;
  padding: 4px 40px 0 8px;
  border: none;
  background: transparent;
}

.doctor-select-wrapper >>> .multiselect__single {
  margin-bottom: 4px;
  font-size: 0.875rem;
  background: transparent;
}

.doctor-select-wrapper >>> .multiselect__placeholder {
  margin-bottom: 4px;
  padding-top: 0;
  font-size: 0.875rem;
}

.doctor-select-wrapper >>> .multiselect__input {
  margin-bottom: 4px;
  font-size: 0.875rem;
  background: transparent;
}

.doctor-select-wrapper >>> .multiselect__select {
  height: 28px;
}

.doctor-select-wrapper >>> .multiselect__content-wrapper {
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
}

/* ================================================
   Mobile Responsive Layout (< 576px)
   ================================================ */
@media (max-width: 575.98px) {
  .doctor-select-wrapper {
    padding-left: 0;
    padding-right: 0;
  }

  .doctor-select-container {
    flex-wrap: wrap;
    padding: 0.375rem;
    padding-left: 0;
    padding-right: 0;
    min-height: auto;
  }

  .doctor-select-container .input-group-prepend {
    border-right: none;
    margin-right: 0;
    padding-right: 0;
    margin-bottom: 0.5rem;
    width: 100%;
    justify-content: flex-start;
  }

  .doctor-select-container > .flex-grow-1 {
    width: 100%;
    flex-basis: 100%;
  }

  .doctor-select-container .d-flex.flex-grow-1 {
    flex-direction: column;
    gap: 0.5rem;
  }

  .specialty-select {
    width: 100% !important;
    min-width: 100% !important;
    margin-right: 0 !important;
  }

  .doctor-multiselect {
    width: 100%;
  }

  .frequent-doctor-item {
    font-size: 0.8rem;
    padding: 0.1rem 0.4rem;
  }
}

/* Z-index fix for modal dropdown visibility */
.doctor-select-wrapper >>> .multiselect__content-wrapper {
  z-index: 1060 !important;
  min-width: 200px;
}

@media (max-width: 575.98px) {
  .doctor-select-wrapper >>> .multiselect__content-wrapper {
    max-height: 200px;
  }
}

/* Small screen enhancements (< 375px) */
@media (max-width: 374.98px) {
  .doctor-select-container {
    padding: 0.25rem;
  }

  .doctor-select-container .input-group-prepend .btn {
    padding: 0.15rem 0.35rem;
    font-size: 0.75rem;
  }

  .frequent-doctor-item {
    font-size: 0.75rem;
    padding: 0.05rem 0.3rem;
  }
}
</style>
